// @ts-expect-error: @rails/request.js is not typed
import { FetchRequest } from '@rails/request.js'
import clsx from 'clsx'
import {
  ArrowLeftRight,
  Check,
  FilePenLine,
  Merge,
  SquareArrowOutUpRight,
  X,
} from 'lucide-react'
import React, { useState } from 'react'
import { ThreeDots } from 'react-loading-icons'
import Select from 'react-select'
import toast from 'react-simple-toasts'

import { adminEmployees, adminRecordInspector } from 'rails_routes'

import { type Employee, type EmployeesByEmployeeId, type Employer } from '../types'
import { toastOptions, toasttOptionsError } from '../utils'
import ButtonPrimary from './reusable/ButtonPrimary'

type FinchDedupeEmployeesProps = {
  employees: EmployeesByEmployeeId
  employer: Employer
  isEmployeesDataLoading: boolean
  fetchEmployeesForEmployer: (employerId: string) => void
}

type SelectOption = {
  label: string
  value: string
}

const BASE_FINCH_URL = '/api/web/admin/finch'

function validateAbilityToMerge (
  selectedManualEmployees: readonly SelectOption[],
  selectedFinchEmployees: readonly SelectOption[],
  employees: EmployeesByEmployeeId
) {
  if (
    selectedManualEmployees.length === 0 ||
    selectedFinchEmployees.length === 0
  ) {
    return false
  }

  if (selectedManualEmployees.length !== selectedFinchEmployees.length) {
    return false
  }

  // If any of the selected duplicate have a member_id or user_id, we should not allow the merge. The API will throw an error.
  if (
    selectedFinchEmployees.some(
      (finchEmployee) =>
        employees[finchEmployee.value].member_id ||
        employees[finchEmployee.value].user_id
    )
  ) {
    return false
  }

  return true
}

function createMergeEmployeesRequestParams (
  selectedManualEmployees: readonly SelectOption[],
  selectedFinchEmployees: readonly SelectOption[]
): [string, string][] {
  return selectedManualEmployees.map((manualEmployee, index) => {
    return [manualEmployee.value, selectedFinchEmployees[index].value]
  })
}

const FinchDedupeEmployees = ({
  employees,
  employer,
  isEmployeesDataLoading,
  fetchEmployeesForEmployer,
}: FinchDedupeEmployeesProps) => {
  const [selectedFinchEmployees, setSelectedFinchEmployees] = useState<
    readonly SelectOption[]
  >([])
  const [selectedManualEmployees, setSelectedManualEmployees] = useState<
    readonly SelectOption[]
  >([])
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)

  function handleSelectManualEmployee (
    updatedSelectedManualEmployees: readonly SelectOption[]
  ) {
    setSelectedManualEmployees(updatedSelectedManualEmployees)
    const recentEmployeeSelected =
      updatedSelectedManualEmployees[updatedSelectedManualEmployees.length - 1]

    if (
      selectedFinchEmployees.length !==
      updatedSelectedManualEmployees.length - 1
    ) {
      return
    }

    if (recentEmployeeSelected) {
      const employee = employees[recentEmployeeSelected.value]
      if (employee) {
        const matchingFinchEmployees = Object.values(employees)
          .filter((e) => !!e.integration_employee_id)
          .filter(
            (e) =>
              `${e.first_name}${e.last_name}` ===
              `${employee.first_name}${employee.last_name}`
          )

        if (!matchingFinchEmployees.length) {
          return
        }

        if (matchingFinchEmployees.length > 1) {
          // If there are multiple employees with the same name, we should warn the user.
          toast(
            `BEWARE: multiple employees have the name ${employee.first_name} ${employee.last_name}`,
            { ...toastOptions, ...toasttOptionsError }
          )
          return
        }

        const matchingFinchEmployee = matchingFinchEmployees[0]

        setSelectedFinchEmployees([
          ...selectedFinchEmployees,
          {
            value: matchingFinchEmployee.id,
            label: `${matchingFinchEmployee.first_name} ${matchingFinchEmployee.last_name}`,
          },
        ])
        toast('Matching Finch employee found!', toastOptions)
      }
    }
  }

  const handleMergeEmployeesRequest = async () => {
    try {
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
    // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const request = new FetchRequest(
        'post',
        `${BASE_FINCH_URL}/employer/merge_employees`,
        {
          body: {
            employer_id: employer?.id,
            employee_merge_ids: createMergeEmployeesRequestParams(
              selectedManualEmployees,
              selectedFinchEmployees
            ),
          },
        }
      )

      const { response } = await request.perform()
      const json = await response.json

      if (response.status !== 201) {
        throw new Error(
          `Error merging employees for ${employer.id}: ${json.message}`
        )
      }
      toast('Employees successfully merged!', toastOptions)
      fetchEmployeesForEmployer(employer.id)
      setSelectedFinchEmployees([])
      setSelectedManualEmployees([])
      setIsConfirmationModalOpen(false)
    } catch (e: any) {
      console.error(e)
    }
  }

  const isValidMergeInput = validateAbilityToMerge(
    selectedManualEmployees,
    selectedFinchEmployees,
    employees
  )

  const selectOptionsMapCallback = (employee: Employee): SelectOption => ({
    value: employee.id,
    label: `${employee.first_name} ${employee.last_name}`,
  })

  const manualEmployeeOptions: SelectOption[] = Object.values(employees)
    .filter((e) => !e.integration_employee_id)
    .map(selectOptionsMapCallback)
  const finchEmployeeOptions: SelectOption[] = Object.values(employees)
    .filter((e) => !!e.integration_employee_id)
    .map(selectOptionsMapCallback)

  return (
    <div>
      <div className='bg-white border rounded-md p-4 mt-4 font-light'>
        <div>
          Please use the below search components to find the employee records
          you'd like to merge.
        </div>
        <div className='pt-4'>
          Employees are broken up into two categories:
          <ol className='list-decimal pl-4'>
            <li>
              <span className='font-bold'>Manually created employees:</span>{' '}
              employees added to Thatch while the Payroll integration was
              disabled
            </li>
            <li>
              <span className='font-bold'>Payroll employees:</span> employees
              added to Thatch via the company's payroll integration
            </li>
          </ol>
        </div>
      </div>
      <div className='flex justify-between pt-6'>
        <div style={{ width: '49%' }}>
          <div className='italic text-sm pb-2'>
            Select Manually Created Employee:
          </div>
          {isEmployeesDataLoading
            ? (
              <div className='flex items-center'>
                <ThreeDots
                  stroke='#4f46e5'
                  fill='#4f46e5'
                  style={{ width: '40px', height: '16px' }}
                />
                <div className='ml-2'>Loading employees</div>
              </div>
              )
            : (
              <Select
                isMulti
                value={selectedManualEmployees}
                options={manualEmployeeOptions}
                onChange={handleSelectManualEmployee}
                inputId='manual-employee-input'
                instanceId='dropdown'
              />
              )}
        </div>
        <div style={{ width: '49%' }}>
          <div className='italic text-sm pb-2'>Select Payroll Employee:</div>
          {isEmployeesDataLoading
            ? (
              <div className='flex items-center'>
                <ThreeDots
                  stroke='#4f46e5'
                  fill='#4f46e5'
                  style={{ width: '40px', height: '16px' }}
                />
                <div className='ml-2'>Loading employees</div>
              </div>
              )
            : (
              <Select
                isMulti
                value={selectedFinchEmployees}
                options={finchEmployeeOptions}
                onChange={setSelectedFinchEmployees}
                inputId='finch-employee-input'
                instanceId='dropdown'
              />
              )}
        </div>
      </div>
      <div className='flex justify-between pt-6'>
        <div style={{ width: '49%' }}>
          <div className='mt-6 flex flex-col justify-center items-center'>
            {selectedManualEmployees.map(({ value }) => {
              const {
                id,
                first_name: firstName,
                last_name: lastName,
                integration_employee_id: finchId,
                member_id: memberId,
                personal_email: personalEmail,
              } = employees[value]

              return (
                <div
                  key={id}
                  className='flex flex-col p-4 font-light w-full bg-white border rounded-md border-grey-500 mb-2 h-24'
                >
                  <EmployeeDetails
                    id={id}
                    firstName={firstName}
                    lastName={lastName}
                    personalEmail={personalEmail}
                  />
                  <div className='flex'>
                    <EmployeeDetailStatus
                      value={finchId}
                      output='Payroll Generated'
                      isEmptyValueDesired
                    />
                    <EmployeeDetailStatus
                      value={memberId}
                      output='Has Begun Onboarding?'
                      isNeutralMetric
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
        <div style={{ width: '49%' }}>
          <div className='mt-6 flex flex-col justify-center items-center'>
            {selectedFinchEmployees.map(({ value }) => {
              const {
                id,
                first_name: firstName,
                last_name: lastName,
                integration_employee_id: finchId,
                member_id: memberId,
                personal_email: personalEmail,
              } = employees[value]

              return (
                <div
                  key={id}
                  className='flex flex-col p-4 font-light w-full bg-white border rounded-md border-grey-500 mb-2 h-24'
                >
                  <EmployeeDetails
                    id={id}
                    firstName={firstName}
                    lastName={lastName}
                    personalEmail={personalEmail}
                  />
                  <div className='flex'>
                    <EmployeeDetailStatus value={finchId} output='Payroll Generated' />
                    <EmployeeDetailStatus
                      value={memberId}
                      output='Has Begun Onboarding?'
                      isEmptyValueDesired
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <div className='flex flex-col justify-center items-center'>
        <div className='mt-6'>
          <MergeIcon isActive={isValidMergeInput} />
        </div>
        <div className='mt-6'>
          <ButtonPrimary
            onClick={() => setIsConfirmationModalOpen(true)}
            isDisabled={!isValidMergeInput}
          >
            Merge Employees
          </ButtonPrimary>
        </div>
      </div>
      {isConfirmationModalOpen && (
        <ConfirmationModal
          employees={employees}
          handleMergeEmployeesRequest={handleMergeEmployeesRequest}
          setIsConfirmationModalOpen={setIsConfirmationModalOpen}
          selectedEmployeesForMerging={createMergeEmployeesRequestParams(
            selectedManualEmployees,
            selectedFinchEmployees
          )}
        />
      )}
    </div>
  )
}

type EmployeeDetailStatusProps = {
  value: string | undefined
  output: string
  isEmptyValueDesired?: boolean
  isNeutralMetric?: boolean
}

const EmployeeDetailStatus = ({
  value,
  output,
  isEmptyValueDesired,
  isNeutralMetric,
}: EmployeeDetailStatusProps) => {
  const isWarning = isEmptyValueDesired ? !!value : !value

  return (
    <div
      className={clsx({
        'flex items-center justify-between border rounded-md px-2 py-1 mr-2 text-sm':
          true,
        'text-green-500 bg-green-200 border-green-500': !isWarning,
        'text-red-500 bg-red-200 border-red-500': isWarning,
        'text-slate-500 bg-slate-200 border-slate-500': isNeutralMetric,
      })}
    >
      {value ? <Check /> : <X />}
      <span>{output}</span>
    </div>
  )
}

const MergeIcon = ({ isActive }: { isActive?: boolean }) => {
  const pulseStyle = {
    strokeDasharray: '150',
    strokeDashoffset: isActive ? '150' : '0',
    animation: isActive ? 'pulseAnimation 3s linear infinite' : '',
  }

  return (
    <>
      {isActive && (
        <style>
          {`
          @keyframes pulseAnimation {
            from {
              stroke-dashoffset: 0;
            }
            to {
              stroke-dashoffset: 200;
            }
          }
        `}
        </style>
      )}
      <Merge
        height='150px'
        width='150px'
        stroke={isActive ? '#AD5BFF' : '#E7E7E7'}
        style={{ rotate: '180deg', ...pulseStyle }}
      />
    </>
  )
}

const EmployeeDetails = ({
  id,
  firstName,
  lastName,
  personalEmail,
}: {
  id: string
  firstName: string
  lastName: string
  personalEmail?: string
}) => {
  return (
    <div className='pb-1 font-semibold flex items-center'>
      {firstName} {lastName}
      <SquareArrowOutUpRight
        onClick={() => window.open(adminRecordInspector.show({ id }), '_blank')}
        className='cursor-pointer ml-1 h-4'
      />
      <FilePenLine
        onClick={() => window.open(adminEmployees.edit({ id }), '_blank')}
        className='cursor-pointer ml-1 h-4'
      />
      {personalEmail && <span className='ml-1 text-slate-400 italic font-light text-xs text-ellipsis'>({personalEmail})</span>}
    </div>
  )
}

const ConfirmationModal = ({
  employees,
  handleMergeEmployeesRequest,
  setIsConfirmationModalOpen,
  selectedEmployeesForMerging,
}: {
  employees: EmployeesByEmployeeId
  handleMergeEmployeesRequest: () => void
  setIsConfirmationModalOpen: (isOpen: boolean) => void
  selectedEmployeesForMerging: [string, string][]
}) => {
  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(5px)',
          zIndex: 1000,
        }}
        onClick={() => setIsConfirmationModalOpen(false)}
      />
      <div
        style={{
          position: 'fixed',
          top: '30%',
          left: '60%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1001,
        }}
        className='p-8 bg-white shadow-md rounded-md border border-gray-200 flex-column items-center justify-center text-gray-500 text-sm font-light'
      >
        <div className='py-2'>
          Please confirm you would like to merge the below{' '}
          {selectedEmployeesForMerging.length} employee(s):
        </div>
        {selectedEmployeesForMerging.map(
          ([manualEmployeeId, finchEmployeeId]) => {
            return (
              <div
                key={`${manualEmployeeId}${finchEmployeeId}`}
                className='flex items-center'
              >
                <div>
                  {employees[manualEmployeeId].first_name}{' '}
                  {employees[manualEmployeeId].last_name}
                </div>
                <ArrowLeftRight className='mx-2' />
                <div>
                  {employees[finchEmployeeId].first_name}{' '}
                  {employees[finchEmployeeId].last_name}
                </div>
              </div>
            )
          }
        )}
        <div className='flex justify-end items-center mt-6'>
          <button
            className='bg-slate-200 text-slate-600 rounded-md p-2 mr-2 border hover:border-slate-600 hover:bg-white hover:text-slate-500 '
            onClick={() => setIsConfirmationModalOpen(false)}
          >
            Close
          </button>
          <ButtonPrimary onClick={handleMergeEmployeesRequest}>
            Confirm Merge
          </ButtonPrimary>
        </div>
      </div>
    </>
  )
}

export default FinchDedupeEmployees
